import {Box, Button, Typography} from '@viptech/react-components'
import {capitalize} from 'lodash'
import {useEffect, useState} from 'react'
import FadeBox from 'src/components/FadeBox'
import InfiniteScrollLoading from 'src/components/loading/InfiniteScrollLoading'
import ModalLateral from 'src/components/modal/ModalLateral'
import Progressbar from 'src/components/Progressbar'
import SlideTypography from 'src/components/SlideTypography'
import {useAuth} from 'src/contexts/AuthContext'
import useFaceDetections from 'src/hooks/api/facial/useFaceDetections'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import addIcon from 'src/images/addPlusIconWhiteSmall.svg'
import arrowIcon from 'src/images/arrowWhiteRightIcon.svg'
import DownloadIcon from 'src/images/DownloadIcon'
import zoomInIcon from 'src/images/zoomInIcon.svg'
import zoomOutIcon from 'src/images/zoomOutIcon.svg'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import {ActivatedActions, FaceDetection} from 'src/services/api'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {formatDateTime} from 'src/utilities/datetimeUtils'
import {useWindowSize} from 'usehooks-ts'
import {TranslateGender} from '../../../utilities/translateGender'
import {ArrowButton} from './FaceBindingFrame'
import {HistoryPhotoFrame} from './HistoryPhotoFrame'
import {HistoryPhotoFrameSkeletonMap} from './HistoryPhotoFrameSkeleton'
import PhotoCard from './PhotoCard'

interface Props {
  faceDetectionSelected: FaceDetection
  cameraName?: string
  companyName?: string
  closeModal: () => void
  onAddFace: () => void
  onBindFace: () => void
  onChangeFaceDetection: (faceDetection: FaceDetection) => void
}

function LabelText({label, text}: {label: string; text: string | number}) {
  const {styledTheme} = usePersonalizedThemeByCompany()

  return (
    <Box display='flex' columnGap='0.5ch'>
      <Typography variant='label' weight={700} size='16px' color={styledTheme.faceRecognition.titleColor}>
        {label}:
      </Typography>
      <Typography variant='label' weight={400} size='16px'>
        {text}
      </Typography>
    </Box>
  )
}

function ActionsText({label, actions}: {label: string; actions: ActivatedActions[]}) {
  const {styledTheme} = usePersonalizedThemeByCompany()

  return (
    <Box display='flex' direction='column' rowGap='0.5ch'>
      <Typography variant='label' weight={700} size='16px' color={styledTheme.faceRecognition.titleColor}>
        {label}:
      </Typography>

      {actions.map((it, index) => (
        <>
          <Typography variant='label' weight={400} size='16px' key={index}>
            {it.reason}: {capitalize(it.dispatchTo)} com {it.success ? 'sucesso' : 'falha'}.
          </Typography>
        </>
      ))}

      {!actions.length && (
        <Typography variant='label' weight={400} size='16px'>
          Nenhuma ação cadastrada
        </Typography>
      )}
    </Box>
  )
}

export default function FaceDetailsModal({faceDetectionSelected, cameraName, companyName, onBindFace, closeModal, onAddFace, onChangeFaceDetection}: Props) {
  const {selectedCompanies} = useCompaniesSelector()

  const {user} = useAuth()

  const faceIds = faceDetectionSelected.face ? [faceDetectionSelected.face.id] : undefined
  const {data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage} = useFaceDetections({companyIds: selectedCompanies.map((it) => it.id), faceIds})
  const windowSize = useWindowSize()
  const isMobile = windowSize.width <= 460

  const [faceDetectionFocused, setFaceDetectionFocused] = useState<FaceDetection>(faceDetectionSelected)
  const [isInDetailedMode, setIsInDetailedMode] = useState(false)
  const [isHoveringPhoto, setIsHoveringPhoto] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const isPersonUnknown = !faceDetectionSelected?.face || !faceDetectionSelected?.face.name

  const nextImage = () => {
    const faceImagesLength = faceDetectionSelected.face?.cropFaceImageKey.length || 0
    setCurrentIndex((prevIndex) => (prevIndex + 1) % faceImagesLength)
  }

  const prevImage = () => {
    const faceImagesLength = faceDetectionSelected.face?.cropFaceImageKey.length || 0

    setCurrentIndex((prevIndex) => (prevIndex - 1 + faceImagesLength) % faceImagesLength)
  }

  const handleFocusedPhoto = (faceDetection: FaceDetection) => {
    setFaceDetectionFocused(faceDetection)
    onChangeFaceDetection(faceDetection)
    setIsInDetailedMode(false)
    setIsHoveringPhoto(false)
  }

  const handleUnfocusPhoto = () => {
    setIsInDetailedMode(false)
    setIsHoveringPhoto(false)
  }

  const onMouseEnter = () => setIsHoveringPhoto(true)
  const onMouseLeave = () => setIsHoveringPhoto(false)

  const onCloseModal = () => {
    setIsHoveringPhoto(false)
    setIsInDetailedMode(false)
    closeModal()
  }

  useEffect(() => {
    setFaceDetectionFocused(faceDetectionSelected)
  }, [faceDetectionSelected])

  return (
    <ModalLateral isOpen={!!faceDetectionSelected} closeModal={onCloseModal} title='Detalhes da face'>
      <Box display='flex' direction='column' rowGap='24px' style={styles.overflow} maxHeight='86vh' flex={1}>
        {isInDetailedMode && (
          <Box>
            <PhotoCard
              width='100%'
              height='390px'
              src={`${process.env.REACT_APP_EVENT_IMAGES_BASE_URL}/${faceDetectionFocused.imageKey}`}
              style={{cursor: 'pointer', aspectRatio: '16/9', objectFit: 'fill'}}
              onClick={handleUnfocusPhoto}>
              <Box position='relative' width='100%' display='flex' justify='flex-end'>
                <img src={zoomOutIcon} alt='close' width={30} height={26} style={{margin: 8}} color='#433942' />
              </Box>
            </PhotoCard>
            {isPersonUnknown && (
              <Box display='flex' rowGap='4px' direction='row' justifyContent='space-between' marginTop='8px'>
                <Box maxWidth={'140px'}>
                  <SlideTypography variant='label' size='14px'>
                    {cameraName || 'DESCONHECIDO'}
                  </SlideTypography>
                </Box>
                <Box display='flex' columnGap='4px'>
                  <Typography variant='label' size='14px'>
                    {formatDateTime(faceDetectionFocused.createdAt).formattedDate}
                  </Typography>
                  <Typography variant='label' size='14px'>
                    |
                  </Typography>
                  <Typography variant='label' size='14px'>
                    {formatDateTime(faceDetectionFocused.createdAt).formattedTime}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box display='flex' flex={1} direction={isMobile && !isPersonUnknown ? 'column' : 'row'} rowGap='24px'>
          <Box
            display='flex'
            direction='column'
            width={isMobile && !isPersonUnknown ? 'auto' : 'fit-content'}
            rowGap='8px'
            marginRight={isPersonUnknown && isInDetailedMode ? '0' : '12px'}>
            <Box display='flex' width='100%' direction='column' rowGap='20px'>
              <Box display='flex' width='100%' justifyContent={'space-between'} columnGap='16px'>
                {!isInDetailedMode && (
                  <PhotoCard
                    src={`${process.env.REACT_APP_CROPPED_IMAGES_BASE_URL}/${faceDetectionFocused.cropImageKey}`}
                    width={isMobile && !isPersonUnknown ? '165px' : '133px'}
                    height={isMobile && !isPersonUnknown ? '200px' : '153px'}
                    onClick={() => setIsInDetailedMode(true)}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}>
                    {!isHoveringPhoto && (
                      <Box position='absolute' width={isMobile && !isPersonUnknown ? '165px' : '133px'} display='flex' justify='flex-end'>
                        <img src={zoomInIcon} alt='close' width={20} height={16} style={{margin: 8}} color='#433942' />
                      </Box>
                    )}

                    <FadeBox isUnmounting={!isHoveringPhoto} onFadeOutComplete={onMouseLeave}>
                      <Box width='100%' display='flex' justify='center' align='center' height='100%'>
                        <img src={zoomInIcon} alt='close' width={52} height={42} color='#433942' />
                      </Box>
                    </FadeBox>
                  </PhotoCard>
                )}
                {!isPersonUnknown && (
                  <Box key={currentIndex} position='relative'>
                    <PhotoCard
                      width='128px'
                      height='153px'
                      src={`${process.env.REACT_APP_TEMPLATE_IMAGES_BASE_URL}/${faceDetectionSelected.face?.cropFaceImageKey?.[currentIndex]}`}
                      style={{cursor: 'pointer'}}
                    />
                    {faceDetectionSelected.face && faceDetectionSelected.face.cropFaceImageKey.length > 1 && (
                      <>
                        <Box display='flex' position='absolute' width='128px' justify='flex-end'>
                          <Typography
                            variant='label'
                            size='10px'
                            weight={700}
                            color={'#0F4C81'}>{`${currentIndex + 1}/${faceDetectionSelected.face.cropFaceImageKey.length}`}</Typography>
                        </Box>
                        <Box display='flex' justifyContent='space-between' position='absolute' width='128px' style={{bottom: '4px'}}>
                          <ArrowButton
                            onClick={(e) => {
                              e.stopPropagation()
                              prevImage()
                            }}>
                            <img src={arrowIcon} alt='Arrow left' style={{transform: 'rotate(180deg)', width: '20px'}} />
                          </ArrowButton>
                          <ArrowButton
                            onClick={(e) => {
                              e.stopPropagation()
                              nextImage()
                            }}>
                            <img src={arrowIcon} alt='Arrow right' style={{width: '20px'}} />
                          </ArrowButton>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
              {!isPersonUnknown && <Progressbar percentage={Math.round(faceDetectionFocused.accuracy * 100)} />}
            </Box>

            {isPersonUnknown && !isInDetailedMode && (
              <Box display='flex' rowGap='4px' direction='column'>
                <Box maxWidth={isInDetailedMode || isPersonUnknown ? '140px' : '282px'}>
                  <SlideTypography variant='label' size='14px'>
                    {cameraName || 'DESCONHECIDO'}
                  </SlideTypography>
                </Box>
                <Box display='flex' columnGap='4px'>
                  <Typography variant='label' size='14px'>
                    {formatDateTime(faceDetectionFocused.createdAt).formattedDate}
                  </Typography>
                  <Typography variant='label' size='14px'>
                    |
                  </Typography>
                  <Typography variant='label' size='14px'>
                    {formatDateTime(faceDetectionFocused.createdAt).formattedTime}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box
            display='flex'
            flex={1}
            direction='column'
            rowGap='20px'
            align='center'
            height='fit-content'
            padding={isMobile && !isPersonUnknown ? '0 4px' : '9'}>
            <Box flex={1} width='99.8%' display='flex' border='1px solid #0F4C81' borderRadius='8px' direction='column'>
              <Box display='flex' direction='column' height='fit-content' padding='16px' rowGap='14px'>
                <LabelText label='Nome' text={faceDetectionFocused.face?.name ?? 'Desconhecido'} />
                {faceDetectionFocused.age && <LabelText label='Idade' text={faceDetectionFocused.age} />}
                {faceDetectionFocused.gender && <LabelText label='Gênero' text={TranslateGender(faceDetectionFocused.gender)} />}
                <LabelText label='Empresa' text={companyName ?? '-'} />
                {faceDetectionFocused.angR && user.email.includes('viptech') && (
                  <LabelText label='Ângulo Direito' text={faceDetectionFocused.angR.toFixed(2)} />
                )}
                {faceDetectionFocused.angL && user.email.includes('viptech') && (
                  <LabelText label='Ângulo Esquerdo' text={faceDetectionFocused.angL.toFixed(2)} />
                )}

                <ActionsText label='Ações' actions={faceDetectionFocused.activatedActions} />
              </Box>
            </Box>
            <Box display='flex' flex={1} width='100%' justifyContent='center' columnGap='12px'>
              {isPersonUnknown && (
                <>
                  <Button fontSize='14px' height='38px' width='180px' onClick={onAddFace}>
                    <img src={addIcon} alt='Adicionar cliente' />
                    Cadastrar face
                  </Button>
                  <Button fontSize='13px' height='38px' width='200px' variant='outlined' onClick={onBindFace}>
                    Vincular a face existente
                  </Button>
                  <Button
                    variant='outlined'
                    height='38px'
                    hoverColor='none'
                    onClick={() =>
                      window.open(`${process.env.REACT_APP_CROPPED_IMAGES_BASE_URL}/${faceDetectionFocused.cropImageKey}`, '_blank', 'noopener,noreferrer')
                    }>
                    <Box display='flex' columnGap='8px' align='center'>
                      <DownloadIcon />
                    </Box>
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {!isPersonUnknown && (
          <Box display='flex' direction='column'>
            <Typography variant='h3' size='16px' weight={700}>
              Listas associadas a esta face
            </Typography>
            <Box display='flex' direction='column' rowGap='4px'>
              {faceDetectionFocused.activatedActions.map((item, index) => (
                <Typography variant='label' size='16px' weight={400} key={index}>
                  {item.reason}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <Box display='flex' direction='column'>
          <Typography variant='h3' size='16px' weight={700}>
            Histórico de detecções
          </Typography>
          <Box
            maxHeight={isPersonUnknown ? '520px' : '380px'}
            padding='6px'
            style={styles.grid}
            onScroll={(e) => {
              const target = e.target as HTMLElement
              const GAP = 10
              const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= GAP
              if (nearBottom && hasNextPage) fetchNextPage()
            }}>
            {isLoading ? (
              <HistoryPhotoFrameSkeletonMap />
            ) : (
              data?.pages
                .map((page) => page.data.data.items)
                .flat()
                .map((item, index) => (
                  <HistoryPhotoFrame
                    key={index}
                    faceDetection={item}
                    onClick={(faceDetection) => handleFocusedPhoto(faceDetection)}
                    isSelected={item.id === faceDetectionFocused.id}
                    isPersonUnknown={isPersonUnknown}
                  />
                ))
            )}
            <InfiniteScrollLoading isLoading={isFetchingNextPage} />
          </Box>
        </Box>
      </Box>
    </ModalLateral>
  )
}

const styles = createStyleSheet({
  overflow: {
    paddingRight: 6,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    '-ms-overflow-style': 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
    overflow: 'auto',
    rowGap: '12px',
    columnGap: '16px',
    scrollbarWidth: 'thin',
    '-ms-overflow-style': 'none',
  },
})
